import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Smartphone, Globe, Lock, Clock, Zap, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const OnlineBanking = () => {
  const features = [
    {
      icon: Smartphone,
      title: "Mobile App",
      description: "Bank on-the-go with our award-winning mobile app",
      benefits: ["Face ID & Touch ID", "Mobile check deposit", "Instant notifications", "Biometric login"]
    },
    {
      icon: Globe,
      title: "Online Portal",
      description: "Full-featured web banking from any device",
      benefits: ["Account management", "Bill pay", "Wire transfers", "Statement downloads"]
    },
    {
      icon: Lock,
      title: "Secure Transactions",
      description: "Bank-grade security protects every transaction",
      benefits: ["256-bit encryption", "Two-factor authentication", "Fraud monitoring", "FDIC insured"]
    },
    {
      icon: Zap,
      title: "Instant Transfers",
      description: "Move money between accounts instantly",
      benefits: ["Real-time transfers", "Zelle integration", "External account linking", "International wire"]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-purple-600 to-purple-800 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">Online Banking</h1>
            <p className="text-lg sm:text-xl text-purple-100 max-w-3xl mx-auto mb-8">
              Bank anywhere, anytime with our secure and easy-to-use online and mobile banking platform
            </p>
            <Button asChild className="bg-white text-purple-600 hover:bg-purple-50">
              <Link to="/user/login">Access Online Banking <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                    <feature.icon className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                  <p className="text-gray-600 mb-6">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-purple-600 rounded-full mr-3" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">Get Started in 3 Easy Steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">1</div>
              <h3 className="text-xl font-bold mb-2">Register</h3>
              <p className="text-gray-600">Create your account in minutes with just your account number</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">2</div>
              <h3 className="text-xl font-bold mb-2">Verify</h3>
              <p className="text-gray-600">Secure your account with two-factor authentication</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">3</div>
              <h3 className="text-xl font-bold mb-2">Start Banking</h3>
              <p className="text-gray-600">Access all your accounts and start managing your finances</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-purple-600 mb-2">99.9%</div>
              <p className="text-gray-600">Uptime</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-purple-600 mb-2">24/7</div>
              <p className="text-gray-600">Access</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-purple-600 mb-2">256-bit</div>
              <p className="text-gray-600">Encryption</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-purple-600 mb-2">100K+</div>
              <p className="text-gray-600">Active Users</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Experience Modern Banking</h2>
          <p className="text-lg sm:text-xl text-purple-100 mb-8">
            Join thousands who trust our secure online banking platform
          </p>
          <Button asChild size="lg" className="bg-white text-purple-600 hover:bg-purple-50">
            <Link to="/user/register">Get Started Now</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default OnlineBanking;
